// ============================================================================
// RECONCILE SERVICE — Personal Finance V2 (Blueprint §6.2 / Fase 5)
// Compara postings Firestore com contas Bling e gera lista de divergências.
// Pure JS — sem dependência de React.
// ============================================================================

import { extractBlingContas, filterBlingByMonth } from '../ports/blingAdapter.js';

/**
 * Tipos de divergência:
 * - ONLY_BLING:       existe no Bling mas não em Firestore
 * - ONLY_FIRESTORE:   existe em Firestore mas sem bling_ids
 * - AMOUNT_MISMATCH:  ambos existem mas valor difere
 * - STATUS_MISMATCH:  Bling aberta mas Firestore PAID, ou vice-versa
 */
export const DIVERGENCE_TYPES = Object.freeze({
    ONLY_BLING:      'ONLY_BLING',
    ONLY_FIRESTORE:  'ONLY_FIRESTORE',
    AMOUNT_MISMATCH: 'AMOUNT_MISMATCH',
    STATUS_MISMATCH: 'STATUS_MISMATCH',
});

/**
 * Gera diff entre postings Firestore e snapshot Bling para um mês.
 *
 * @param {object[]} firestorePostings — postings do mês (já filtrados)
 * @param {object}   blingSnapshot     — recta_bling_snapshot.json completo
 * @param {string}   yearMonth         — 'YYYY-MM'
 * @returns {{ divergences: object[], summary: object, blingMeta: object }}
 */
export function diffWithBling(firestorePostings, blingSnapshot, yearMonth) {
    const { pagar, receber, meta } = extractBlingContas(blingSnapshot);

    // Filtra Bling por mês
    const blingPagar = filterBlingByMonth(pagar, yearMonth);
    const blingReceber = filterBlingByMonth(receber, yearMonth);

    const divergences = [];

    // Index Firestore por bling_ids
    const fsByBlingPagarId = new Map();
    const fsByBlingReceberId = new Map();
    const fsWithoutBling = [];

    for (const p of firestorePostings) {
        if (p.bling_ids?.conta_pagar_id) {
            fsByBlingPagarId.set(p.bling_ids.conta_pagar_id, p);
        } else if (p.bling_ids?.conta_receber_id) {
            fsByBlingReceberId.set(p.bling_ids.conta_receber_id, p);
        } else if (p.group !== 'RECEITAS') {
            // Despesas sem bling_ids — candidatas a ONLY_FIRESTORE
            fsWithoutBling.push(p);
        }
    }

    // 1. Contas a pagar: Bling vs Firestore
    const matchedBlingPagarIds = new Set();
    for (const bc of blingPagar) {
        const fsMatch = fsByBlingPagarId.get(bc.blingId);
        if (!fsMatch) {
            divergences.push({
                type: DIVERGENCE_TYPES.ONLY_BLING,
                bling: bc,
                firestore: null,
                detail: `Conta a pagar Bling #${bc.blingId} (${bc.contactName}, R$${bc.value}) sem correspondente em Firestore.`,
            });
        } else {
            matchedBlingPagarIds.add(bc.blingId);
            // Checar valor
            if (Math.abs(bc.value - Number(fsMatch.value)) > 0.01) {
                divergences.push({
                    type: DIVERGENCE_TYPES.AMOUNT_MISMATCH,
                    bling: bc,
                    firestore: fsMatch,
                    detail: `Valor difere: Bling R$${bc.value} vs Firestore R$${fsMatch.value}.`,
                });
            }
            // Checar status
            const blingOpen = bc.situacao === 1 || bc.situacao === 4;
            const fsPaid = fsMatch.status === 'PAID';
            if (blingOpen && fsPaid) {
                divergences.push({
                    type: DIVERGENCE_TYPES.STATUS_MISMATCH,
                    bling: bc,
                    firestore: fsMatch,
                    detail: `Bling ABERTA mas Firestore PAID.`,
                });
            }
            if (!blingOpen && !fsPaid) {
                divergences.push({
                    type: DIVERGENCE_TYPES.STATUS_MISMATCH,
                    bling: bc,
                    firestore: fsMatch,
                    detail: `Bling BAIXADA mas Firestore ${fsMatch.status}.`,
                });
            }
        }
    }

    // 2. Contas a receber: Bling vs Firestore
    for (const bc of blingReceber) {
        const fsMatch = fsByBlingReceberId.get(bc.blingId);
        if (!fsMatch) {
            divergences.push({
                type: DIVERGENCE_TYPES.ONLY_BLING,
                bling: bc,
                firestore: null,
                detail: `Conta a receber Bling #${bc.blingId} (${bc.contactName}, R$${bc.value}) sem correspondente em Firestore.`,
            });
        }
    }

    // 3. Firestore sem bling_ids (despesas que deveriam estar no Bling PJ)
    for (const fs of fsWithoutBling) {
        // Só reporta despesas com account PJ ou vinculo
        const ctx = [fs.account, fs.description].join(' ').toLowerCase();
        const isPjRelated = ctx.includes('c6') || ctx.includes('pj') || ctx.includes('retirada') || ctx.includes('recta');
        if (isPjRelated && fs.status !== 'CANCELLED') {
            divergences.push({
                type: DIVERGENCE_TYPES.ONLY_FIRESTORE,
                bling: null,
                firestore: fs,
                detail: `Despesa PJ "${fs.description}" (R$${fs.value}) sem bling_ids vinculado.`,
            });
        }
    }

    const summary = {
        totalBlingPagar: blingPagar.length,
        totalBlingReceber: blingReceber.length,
        totalFirestore: firestorePostings.length,
        matched: matchedBlingPagarIds.size,
        divergences: divergences.length,
        byType: {
            [DIVERGENCE_TYPES.ONLY_BLING]: divergences.filter(d => d.type === DIVERGENCE_TYPES.ONLY_BLING).length,
            [DIVERGENCE_TYPES.ONLY_FIRESTORE]: divergences.filter(d => d.type === DIVERGENCE_TYPES.ONLY_FIRESTORE).length,
            [DIVERGENCE_TYPES.AMOUNT_MISMATCH]: divergences.filter(d => d.type === DIVERGENCE_TYPES.AMOUNT_MISMATCH).length,
            [DIVERGENCE_TYPES.STATUS_MISMATCH]: divergences.filter(d => d.type === DIVERGENCE_TYPES.STATUS_MISMATCH).length,
        },
    };

    return { divergences, summary, blingMeta: meta, blingKpis: extractBlingContas(blingSnapshot).kpis };
}

/**
 * Health score: percentual de lançamentos corretamente pareados.
 */
export function healthScore(firestorePostings) {
    if (!firestorePostings.length) return { score: 100, orphans: 0, total: 0 };

    const withPair = firestorePostings.filter(p => p.pairId);
    const pairIds = new Set(withPair.map(p => p.pairId));
    let completePairs = 0;
    let orphans = 0;

    for (const pid of pairIds) {
        const members = withPair.filter(p => p.pairId === pid);
        const hasIncome = members.some(m => m.group === 'RECEITAS');
        const hasExpense = members.some(m => m.group !== 'RECEITAS');
        if (hasIncome && hasExpense) {
            completePairs++;
        } else {
            orphans += members.length;
        }
    }

    const singles = firestorePostings.filter(p => !p.pairId).length;
    const total = completePairs * 2 + orphans + singles;
    const healthy = completePairs * 2 + singles;
    const score = total > 0 ? Math.round((healthy / total) * 100) : 100;

    return { score, orphans, total, completePairs, singles };
}
